/**
 * HealLandingVFX — a heal lands visibly on whoever it healed (plan 29 F9,
 * lane CA 2026-10-11).
 *
 * Before, a server heal drew only its number: Flash Mend's splash played on
 * the caster's screen at the press (the recipient as the caster guessed it,
 * before the wind-up and the server's friendly check), observers saw the
 * splash at the caster, and an area heal (Prayer of Mending, Chain Heal,
 * Healing Stream Totem's pulses) showed nothing on the friends it reached.
 *
 * Now every `playerHealed` the server broadcasts — the one record of who was
 * actually healed — plays the ability's authored landing (its TOML
 * `impact_effect`, a registered one-shot burst: `authoredImpactBurstId`) on
 * that player, on every screen, following them. Heals only ever land on a
 * PLAYER: a target that resolves to no player (a mob id, a player who left)
 * draws nothing, so a heal can never draw on an enemy. Heal-over-time ticks
 * are not `playerHealed` (they are the status's state).
 */
import type { PlayerHealedEvent } from '../../../shared/types/NetworkMessages';
import { authoredImpactBurstId } from '../authoredImpact';
import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';

export interface HealLandingDeps {
  /** The healed player's body centre — the local player or a remote one — or null. */
  playerPosition(sessionId: string): { x: number; y: number } | null;
  play(effectId: string, x: number, y: number, overrides: { targetId: string; abilityId: string }): void;
}

/** The landing the heal of `abilityId` plays on its recipient, if it authors one. */
export function healLandingEffectId(abilityId: string | null | undefined): string | undefined {
  // The same wire event also reports lifesteal and on-kill healing. Their
  // damaging impact is not a heal bloom on the attacker who recovered HP.
  if (!abilityId || getAbilityData(abilityId)?.abilityType !== 'heal') return undefined;
  return authoredImpactBurstId(abilityId);
}

export class HealLandingVFX {
  constructor(private readonly deps: HealLandingDeps) {}

  onHeal(ev: Pick<PlayerHealedEvent, 'targetSid' | 'ability_id' | 'amount'> | null | undefined): void {
    if (!ev?.targetSid || !(ev.amount > 0)) return;
    const effectId = healLandingEffectId(ev.ability_id);
    if (!effectId) return;
    const at = this.deps.playerPosition(ev.targetSid);
    if (!at) return;
    this.deps.play(effectId, at.x, at.y, { targetId: ev.targetSid, abilityId: ev.ability_id });
  }
}
